import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Location } from 'history';
import {
  getAuthorisedSignatoryList,
  updateAuthorisedSignatory,
} from '../../redux-store/actions/userManagement';
import {
  AddAuthorisedSignatoryRequestBody,
  AuthorisedSignatories,
} from '../../redux-store/types/api-types';
import { ColumnType } from '../DataTable';
import UserMgmtListPage, {
  NameLinkCell,
  buildAccessColumn,
  buildActionsColumn,
  formatPhone,
  formatUserName,
  matchesUserSearch,
} from './UserMgmtListPage';

export default function AuthorisedSignatory({
  location,
}: {
  location: Location<{ distributorId: string }>;
}): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const { distributorId } = location.state || {};

  return (
    <UserMgmtListPage<AuthorisedSignatories>
      loadDeps={[distributorId]}
      loadData={async () => {
        if (!distributorId) {
          history.replace('/user-management');
          return [];
        }
        return (await dispatch(
          getAuthorisedSignatoryList({ distributorId: Number(distributorId) })
        )) as unknown as AuthorisedSignatories[];
      }}
      searchPlaceholder="Search for Authorised Signatories"
      inviteLabel="Add Authorised Signatory"
      onInvite={() =>
        history.push('add-authorised-signatory', { distributorId: distributorId.toString() })
      }
      filterRow={(row, q) =>
        matchesUserSearch(q, [
          formatUserName(row.user),
          row.user?.email,
          `${row.user?.countryCode || ''}${row.user?.phone || ''}`,
        ])
      }
      getColumns={({ onToggleAccess }) => {
        const columns: ColumnType[] = [
          {
            header: 'Name',
            align: 'left',
            headerAlign: 'left',
            renderCell: (row: AuthorisedSignatories) => (
              <NameLinkCell
                label={formatUserName(row.user)}
                to={{
                  pathname: 'authorised-signatory-details',
                  state: { authorisedSignatoryId: row.id },
                }}
              />
            ),
          },
          {
            header: 'Mobile Number',
            align: 'left',
            headerAlign: 'left',
            valueGetter: (row: AuthorisedSignatories) => formatPhone(row.user),
          },
          {
            header: 'Email Id',
            align: 'left',
            headerAlign: 'left',
            valueGetter: (row: AuthorisedSignatories) => row.user?.email || 'N/A',
          },
          buildAccessColumn(onToggleAccess),
          buildActionsColumn((row: AuthorisedSignatories) => [
            {
              label: 'View Details',
              icon: '/images/view-application.svg',
              to: {
                pathname: 'authorised-signatory-details',
                state: { authorisedSignatoryId: row.id },
              },
            },
            {
              label: 'Edit Details',
              icon: '/images/edit.svg',
              to: {
                pathname: 'edit-authorised-signatory',
                state: { authorisedSignatoryId: row.id },
              },
            },
          ]),
        ];
        return columns;
      }}
      onAccessChange={async (row, nextActive) =>
        (await dispatch(
          updateAuthorisedSignatory(row.id as number, {
            ...(row as unknown as AddAuthorisedSignatoryRequestBody),
            isActive: nextActive,
          })
        )) as unknown as AuthorisedSignatories
      }
    />
  );
}
